/**
 * Cached read of the whole designer configuration tree.
 *
 * The tree is a single large GET that triggers a server-side rebuild, and App
 * mode asks for it on every screen open — twice per list (item + list schema),
 * once per record form, once per table-name resolution. Restoring a multi-tab
 * session used to fire one full download per leg, all at the same instant.
 *
 * Staleness window: 30s per connection (same TTL as `entitiesCache`, which
 * backs the FK pickers and `useObjectLabel`). A designer's save therefore
 * shows up in App mode within 30 seconds; there is no push invalidation.
 * `invalidateDesignerTreeCache()` is the escape hatch for callers that know a
 * save just landed (and for tests).
 */
import { ApiService } from '@/utils/api'
import type { ConfigNode } from '@/designer/types/config.types'
import { createTtlCache } from './ttlCache'

const TREE_TTL_MS = 30_000

const treeCache = createTtlCache<ConfigNode[]>(TREE_TTL_MS)

export function fetchDesignerTree(connectionId: string): Promise<ConfigNode[]> {
  return treeCache.get(connectionId, async () => {
    const res = await ApiService.get<{ tree: ConfigNode[] | null }>(
      `/v1/designer/config-tree?connection_id=${encodeURIComponent(connectionId)}`,
    )
    return res?.tree ?? []
  })
}

/** Drop the cached tree for one connection, or all of them when called bare. */
export function invalidateDesignerTreeCache(connectionId?: string): void {
  treeCache.invalidate(connectionId)
}
